import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

interface PWAErrorRecoveryProps {
  isVisible: boolean;
  errorType?:
    | 'white-screen'
    | 'resource-error'
    | 'javascript-error'
    | 'react-error';
  onRetry?: () => void;
}

const PWAErrorRecovery: React.FC<PWAErrorRecoveryProps> = ({
  isVisible,
  errorType = 'white-screen',
  onRetry,
}) => {
  const [recoveryStep, setRecoveryStep] = useState<
    'detecting' | 'clearing' | 'updating' | 'reloading'
  >('detecting');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isVisible) {
      setRecoveryStep('detecting');
      setProgress(0);
      return;
    }

    // Simulate recovery progress
    const recoverySteps = [
      { step: 'detecting', duration: 1000, progress: 25 },
      { step: 'clearing', duration: 1500, progress: 50 },
      { step: 'updating', duration: 2000, progress: 75 },
      { step: 'reloading', duration: 1000, progress: 100 },
    ];

    let currentStepIndex = 0;
    const startTime = Date.now();

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const currentStep = recoverySteps[currentStepIndex];

      if (elapsed >= currentStep.duration) {
        currentStepIndex++;
        if (currentStepIndex < recoverySteps.length) {
          setRecoveryStep(recoverySteps[currentStepIndex].step as any);
          setProgress(recoverySteps[currentStepIndex].progress);
        } else {
          setProgress(100);
          // Auto-reload after recovery animation
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      } else {
        // Smooth progress animation
        const stepProgress =
          (elapsed / currentStep.duration) * currentStep.progress;
        setProgress(stepProgress);
      }

      if (currentStepIndex < recoverySteps.length) {
        requestAnimationFrame(updateProgress);
      }
    };

    updateProgress();
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  const getErrorMessage = () => {
    switch (errorType) {
      case 'white-screen':
        return 'Application appears to be in an inconsistent state';
      case 'resource-error':
        return 'Some application resources failed to load';
      case 'javascript-error':
        return 'Application encountered JavaScript errors';
      case 'react-error':
        return 'Application interface failed to render';
      default:
        return 'Application needs to be refreshed';
    }
  };

  const getStepMessage = () => {
    switch (recoveryStep) {
      case 'detecting':
        return 'Detecting application issues...';
      case 'clearing':
        return 'Clearing cached data...';
      case 'updating':
        return 'Updating application...';
      case 'reloading':
        return 'Reloading application...';
      default:
        return 'Recovering...';
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 3,
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          maxWidth: 400,
          width: '100%',
        }}
      >
        {/* App Icon/Logo */}
        <Box
          sx={{
            width: 80,
            height: 80,
            backgroundColor: '#1976d2',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 2rem',
            color: 'white',
            fontSize: '2rem',
            fontWeight: 'bold',
          }}
        >
          S
        </Box>

        {/* Error Alert */}
        <Alert
          severity="warning"
          sx={{
            marginBottom: 3,
            textAlign: 'left',
          }}
        >
          <Typography variant="body2">{getErrorMessage()}</Typography>
        </Alert>

        {/* Recovery Progress */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recovering Application
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {getStepMessage()}
          </Typography>

          {/* Progress Bar */}
          <Box sx={{ marginTop: 2 }}>
            <Box
              sx={{
                width: '100%',
                height: 8,
                backgroundColor: '#e0e0e0',
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${progress}%`,
                  height: '100%',
                  backgroundColor: '#1976d2',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Manual Retry Option */}
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="body2" color="text.secondary">
            If recovery doesn't complete automatically, you can:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              justifyContent: 'center',
              marginTop: 1,
            }}
          >
            <Box
              onClick={onRetry || (() => window.location.reload())}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                padding: '8px 16px',
                backgroundColor: '#1976d2',
                color: 'white',
                borderRadius: 1,
                cursor: 'pointer',
                fontSize: '0.875rem',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: '1rem' }} />
              Retry Now
            </Box>
          </Box>
        </Box>

        {/* Additional Help */}
        <Box sx={{ marginTop: 3 }}>
          <Typography variant="caption" color="text.secondary">
            If problems persist, try closing and reopening the app
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PWAErrorRecovery;
